import { defineRecipe } from '@chakra-ui/react';

// Per-palette colours (including v2's gray/darkgray special cases) come from
// the btn* semantic tokens in ../buttonPalettes.ts. mode(light, dark) calls
// collapsed to their light value — the portal never configures color mode.
const ghost = {
  color: 'colorPalette.btnGhostColor',
  bg: 'transparent',
  _hover: { bg: 'colorPalette.btnGhostHover' },
  _active: { bg: 'colorPalette.btnGhostActive' },
  _focus: { boxShadow: '0 0 0 3px {colors.colorPalette.btnFocusRing}' },
} as const;

const solid = {
  bg: 'colorPalette.btnSolidBg',
  color: 'colorPalette.btnSolidColor',
  _hover: { bg: 'colorPalette.btnSolidHover' },
  _active: { bg: 'colorPalette.btnSolidActive' },
  _focus: { boxShadow: '0 0 0 3px {colors.colorPalette.btnFocusRing}' },
} as const;

// v3's button recipe pins icon dimensions on every entry of its size axis
// (`_icon: { width, height }`), so icons no longer follow the button's
// fontSize as they did in v2. Our sizes deep-merge with v3's same-named ones,
// which drags the pin in; `base` can't undo it, since cva applies variants
// over base. So the reset has to be repeated per size.
//
// Scoped to react-icons, which emit width="1em" — an SVGR asset carries its
// own px size and rendered at that size in v2, so it must not be caught here.
// No `:where()`, so this outranks v3's `_icon` rule (same layer, same origin)
// on specificity rather than on source order.
//
// This covers plain buttons and menu triggers. `IconButton` additionally sets
// `_icon: { fontSize: '1.2em' }` as a style prop — unlayered, so it beats
// recipe CSS and this cannot reach it; use ui-core's `IconButton` for those.
const icon = {
  '& svg[width="1em"]': { width: '1em', height: '1em' },
} as const;

const buttonRecipe = defineRecipe({
  base: {
    colorPalette: 'primary',
    lineHeight: 1,
    borderRadius: 'md',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    _focus: {
      boxShadow: 'outline',
    },
    // v3's stock keyboard focus ring reads colorPalette.focusRing, which
    // `primary` doesn't define, so it rendered dark grey. Same colour as the
    // input focus border (./input.ts).
    focusRingColor: 'primary.500',
    _disabled: {
      opacity: 0.4,
      cursor: 'not-allowed',
      boxShadow: 'none',
    },
  },
  variants: {
    variant: {
      ghost,
      outline: {
        // longhands — a colourless `border` shorthand resets border-color to
        // currentColor and renders black
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'colorPalette.btnOutlineBorder',
        background: 'transparent',
        ...ghost,
      },
      solid,
      // v2: ghost(gray) at rest, solid(palette) when NavLink adds .active —
      // used for pill nav filters
      activeSolid: {
        ...ghost,
        color: '{colors.gray.btnGhostColor}',
        _hover: { bg: '{colors.gray.btnGhostHover}' },
        _active: { bg: '{colors.gray.btnGhostActive}' },
        textTransform: 'initial',
        '&.active': solid,
      },
      link: {
        padding: 0,
        height: 'auto',
        lineHeight: 'normal',
        color: 'colorPalette.btnGhostColor',
        _hover: { textDecoration: 'underline' },
        _active: { color: 'colorPalette.btnLinkActive' },
      },
      unstyled: {
        bg: 'none',
        color: 'inherit',
        display: 'inline',
        lineHeight: 'inherit',
        m: 0,
        p: 0,
      },
    },
    size: {
      lg: {
        borderRadius: 'full',
        h: 12,
        minW: 20,
        fontSize: 'lg',
        textTransform: 'initial',
        px: 6,
        ...icon,
      },
      md: {
        borderRadius: 'full',
        h: 10,
        minW: 20,
        fontSize: 'sm',
        px: 4,
        ...icon,
      },
      sm: {
        borderRadius: 'full',
        h: 8,
        minW: 8,
        fontSize: 'sm',
        px: 3,
        ...icon,
      },
      xs: {
        borderRadius: 'full',
        h: 6,
        minW: 6,
        fontSize: 'xs',
        px: 2,
        ...icon,
      },
    },
  },
  defaultVariants: {
    variant: 'solid',
    size: 'md',
  },
});

export default buttonRecipe;
